import { CaretDownFilled, CaretUpFilled, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Menu, message, Modal, Row, Space, Spin, Typography, UploadFile } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useIsFetching, useQueryClient } from 'react-query';
import { Quill } from 'react-quill-new';
import { useSearchParams } from 'react-router-dom';

import useAPIClient from '../../../api/useAPIClient';
import useAPIQueries from '../../../api/useAPIQueries';
import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery, { InfiniteResponse } from '../../../api/useInfiniteAPIQuery';
import { AdditionalQuestionsFormValues } from '../../../components/AdditionalQuestionsForm';
import { EmailFormValues } from '../../../components/EmailForm';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import PageContent from '../../../components/PageContent';
import { fixUnorderedListInQuill } from '../../../components/RichEditor/RichEditor';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import useStagesMutation from '../../../hooks/useStagesMutation';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import {
  ExpandedInterviewScriptTemplateCollection,
  ExpandedStageCollection,
  JobPostingId,
  SerialID,
  StageCollection,
} from '../../../types';
import { isDescriptionEmpty } from '../PublicationForm/PublicationInfoForm';
import StageFormItem from './StageFormItem';

export interface StageFormFormat extends EmailFormValues {
  id?: SerialID;
  name: string;
  type: StageCollection['type'];
  sub_type: StageCollection['sub_type'];
  fixed: boolean;
  sequence: number | null;
  send_tests: boolean;
  testTypeList: string[];
  questions?: AdditionalQuestionsFormValues['questions'];
  interview_script_template: number | null;
  lock_interview_script: boolean;
  interview_script_name: string;
  interview_script_content?: string;
}

interface StagesFormValues {
  stages: StageFormFormat[];
}

const StagesForm = () => {
  const { t } = useTranslation();

  const { id, tenant } = useTypedParams({ id: JobPostingId, tenant: String });
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const [leavePage, setLeavePage] = useState<boolean>(false);
  const [setIsDirty] = useDiscardChangesModal(form);

  const [selectedStageIdx, setSelectedStageIdx] = useState(0);
  const [isGeneratingInterviewScript, setIsGeneratingInterviewScript] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const questionsPages = useInfiniteAPIQuery(['/questions/', { params: { job_posting_id: id } }]);

  const questions = usePrefetchPages(questionsPages, (data) => data.results);

  const functions = useAPIQuery('/mindsight/ats-integrations/functions/');

  const tests = functions.data?.results
    .find((fn) => fn.id === jobPosting.data?.function_id)
    ?.required_assessments?.map(({ key }) => key);

  const stagesPages = useInfiniteAPIQuery<
    '/stages/',
    'GET',
    InfiniteResponse<ExpandedStageCollection>
  >([
    `/stages/`,
    {
      params: {
        job_posting_id: id,
        expand: 'interview_script_content,email_communication_template',
      },
    },
  ]);

  const stages = usePrefetchPages(stagesPages, (data) => data.results);

  const api = useAPIClient();

  const recruitmentProcesses = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
      },
    },
  ]);

  const subscribedStage = stages.find((stage) => stage.type === 'SUBSCRIBED');

  const subscribedStageTemplateAttachments = useAPIQueries(
    subscribedStage?.email_communication_template?.attachments.map((attachmentId) => [
      `/attachments/${attachmentId}/`,
    ]) ?? [],
    {
      enabled: Boolean(
        subscribedStage?.email_communication_template &&
          subscribedStage.email_communication_template.attachments.length > 0
      ),
    }
  );

  const interviewScriptTemplatePages = useInfiniteAPIQuery<
    '/interview-script-templates/',
    'GET',
    InfiniteResponse<ExpandedInterviewScriptTemplateCollection>
  >([
    '/interview-script-templates/',
    {
      params: { expand: 'email_communication_template' },
    },
  ]);

  const interviewScriptTemplates = usePrefetchPages(
    interviewScriptTemplatePages,
    (data) => data.results
  );

  const isEmailCommunicationTemplateFetching = useIsFetching('/email-communication-templates/');

  const removeStage = async (stage: StageFormFormat, removeFn: () => void) => {
    if (!stage.id) {
      return removeFn();
    }

    const interviews = await api.get('/interviews/', {
      params: { stage: stage.id, page_size: 10 },
    });

    if (interviews.data.count > 0) {
      Modal.warning({
        title: t('Ação não permitida!'),
        content: (
          <>
            <Typography.Text>
              <Trans>
                Não é possível remover esta etapa, pois há entrevistas associadas a ela.
              </Trans>
            </Typography.Text>
          </>
        ),
      });
      return;
    }

    const recruitmentProcesses = await api.get('/recruitment-processes/', {
      params: { stage_id: stage.id, page_size: 10 },
    });

    if (recruitmentProcesses.data.results.length === 0) {
      removeFn();
    } else {
      Modal.warning({
        title: t('Ação não permitida!'),
        content: (
          <>
            <Typography.Text>
              <Trans>Não é possível remover esta etapa, pois há candidatos associados a ela.</Trans>
            </Typography.Text>
            <br />
            <Typography.Text>
              <Trans>Movimente os candidatos para outra etapa antes de removê-la.</Trans>
            </Typography.Text>
          </>
        ),
      });
    }
  };

  const mutation = useStagesMutation({
    onSuccess: () => {
      setIsDirty(false);
      message.success(t('Etapas atualizadas'), 6);

      if (searchParams.get('new')) {
        navigate({
          pathname: '/:tenant/opportunities/:id/change/publication/',
          params: {
            tenant,
            id,
            new: 1,
          },
        });
      } else if (leavePage) {
        navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries('/stages/');
      await queryClient.invalidateQueries('/email-communication-templates/');
      await queryClient.invalidateQueries('/attachments/');
      await queryClient.invalidateQueries('/recruitment-processes/');
      await queryClient.invalidateQueries('/interview-script-templates/');
      await queryClient.invalidateQueries('/questions/');
    },
  });

  const messageInitialValue = subscribedStage?.email_communication_template?.message || '';

  return (
    <ErrorResult error={stagesPages.error || jobPosting.error || functions.error}>
      <Helmet>
        <title>
          {t('Fluxo de etapas')} | {jobPosting.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <PageContent>
        <Spin
          spinning={
            stagesPages.isLoading ||
            jobPosting.isLoading ||
            functions.isLoading ||
            questionsPages.isLoading ||
            recruitmentProcesses.isLoading ||
            isEmailCommunicationTemplateFetching > 0
          }
        >
          <Form.Provider
            onFormFinish={(name, { forms: { jobPostingRegistrationTemplate, stagesForm } }) => {
              if (name === 'stagesForm' && jobPostingRegistrationTemplate) {
                jobPostingRegistrationTemplate.submit();
              }
            }}
          >
            <Form<StagesFormValues>
              form={form}
              name="stagesForm"
              layout="vertical"
              validateMessages={{
                required: t('Preenchimento obrigatório'),
              }}
              initialValues={{
                stages: stages.map<
                  Omit<StageFormFormat, 'sub_type'> & {
                    sub_type: StageFormFormat['sub_type'] | '';
                  }
                >((stage) => {
                  const interviewScript = interviewScriptTemplates.find(
                    ({ id }) => stage.interview_script_template === id
                  );

                  return {
                    ...stage,
                    sub_type:
                      stage.type === 'CUSTOM' && stage.sub_type === 'LEGACY' ? '' : stage.sub_type,
                    ...(stage.type === 'SUBSCRIBED'
                      ? {
                          email_communication_template:
                            stage.email_communication_template &&
                            !stage.email_communication_template?.individual
                              ? stage.email_communication_template?.id
                              : null,
                          testTypeList: stage.tests,
                          subject: stage.email_communication_template?.subject || '',
                          message: messageInitialValue,
                          fromName: stage.email_communication_template?.from_name || '',
                          attachments:
                            subscribedStageTemplateAttachments.data?.map<UploadFile>(
                              (subscribedStageTemplateAttachment) => ({
                                uid: subscribedStageTemplateAttachment.id.toString() as string,
                                name: subscribedStageTemplateAttachment.filename as string,
                                status: 'done',
                                url: subscribedStageTemplateAttachment.file,
                                type: subscribedStageTemplateAttachment.mimetype,
                              })
                            ) ?? [],
                          questions: questions.map((question) => ({
                            id: question.id,
                            type: question.type,
                            options: question.alternatives?.map((alternative) => ({
                              id: alternative.id,
                              description: alternative.description,
                            })),
                            description: question.description,
                            required: question.required,
                          })),
                        }
                      : {
                          testTypeList: stage.tests,
                          email_communication_template: stage.email_communication_template
                            ?.individual
                            ? 0
                            : interviewScript?.email_communication_template?.id || 0,
                          subject:
                            stage.email_communication_template?.subject ||
                            interviewScript?.email_communication_template?.subject ||
                            '',
                          message:
                            stage.email_communication_template?.message ||
                            interviewScript?.email_communication_template?.message ||
                            '',
                          fromName:
                            stage.email_communication_template?.from_name ||
                            interviewScript?.email_communication_template?.from_name ||
                            '',
                          attachments:
                            stage.email_communication_template?.attachments?.map<UploadFile>(
                              (id) => ({ name: `${id}`, uid: `${id}` })
                            ) ||
                            interviewScript?.email_communication_template?.attachments?.map<UploadFile>(
                              (id) => ({ name: `${id}`, uid: `${id}` })
                            ) ||
                            [],
                          interview_script_template: interviewScript?.id || 0,
                          interview_script_content:
                            stage.interview_script_content || interviewScript?.content || '',
                        }),
                  };
                }),
              }}
              onFinish={(variables) => {
                mutation.mutate({
                  stages: variables.stages.map((x) => ({
                    ...x,
                    interview_script_content: fixUnorderedListInQuill(
                      x.interview_script_content ?? ''
                    ),
                    message: fixUnorderedListInQuill(x.message),
                  })),

                  jobPostingId: id,
                });
              }}
              onValuesChange={(changedValues) => {
                const keys = Object.keys(changedValues);
                if (keys.length === 1 && keys[0] === 'message') {
                  const tempContainer = document.createElement('div');
                  const editor = new Quill(tempContainer);
                  const initialDelta = editor.clipboard.convert({ html: messageInitialValue });
                  const currentDelta = editor.clipboard.convert({ html: changedValues.message });
                  const diff = initialDelta.diff(currentDelta);
                  if (!diff?.ops?.length) {
                    setIsDirty(false);
                  } else {
                    setIsDirty(true);
                  }
                } else {
                  setIsDirty(form.isFieldsTouched());
                }
              }}
            >
              <Space direction="vertical" size={16}>
                <Form.Row>
                  <Form.List name="stages">
                    {(fields, { add, move, remove }) => (
                      <>
                        <Form.Col span={6}>
                          <Form.Item noStyle shouldUpdate={() => true}>
                            {(form) => (
                              <Menu
                                mode="inline"
                                selectedKeys={[fields[selectedStageIdx]?.key.toString()]}
                                onClick={({ key }) => {
                                  switch (key) {
                                    case 'addStage':
                                      break;
                                    default:
                                      if (!isGeneratingInterviewScript) {
                                        const idx = fields.findIndex(
                                          (field) => field.key.toString() === key
                                        );
                                        setSelectedStageIdx(idx);
                                      }
                                      break;
                                  }
                                }}
                                disabled={isGeneratingInterviewScript}
                                items={fields.flatMap(({ key, name }, idx) =>
                                  form.getFieldValue(['stages', name, 'type']) === 'CUSTOM'
                                    ? {
                                        key,
                                        danger:
                                          form
                                            .getFieldsError()
                                            .filter(
                                              (error) =>
                                                error.name[0] === 'stages' &&
                                                error.name[1] === name &&
                                                error.errors.length
                                            ).length > 0,
                                        disabled: isGeneratingInterviewScript,
                                        label: (
                                          <Row gutter={[16, 16]}>
                                            <Col span={2}>
                                              <DeleteOutlined
                                                data-testid={`delete-stage-button-${idx}`}
                                                title={`Remover ${
                                                  form.getFieldValue(['stages', name, 'name']) ||
                                                  t('Nome da etapa')
                                                }`}
                                                onClick={async () => {
                                                  const stage = form.getFieldValue([
                                                    'stages',
                                                    name,
                                                  ]);
                                                  if (isGeneratingInterviewScript) return;
                                                  await removeStage(stage, () => {
                                                    remove(idx);
                                                  });
                                                }}
                                              />
                                            </Col>
                                            <Col
                                              span={17}
                                              onClick={() => {
                                                if (isGeneratingInterviewScript) return;
                                                setSelectedStageIdx(idx);
                                              }}
                                            >
                                              <Typography.Text
                                                type={
                                                  form.getFieldValue(['stages', name, 'name']) ===
                                                  ''
                                                    ? 'secondary'
                                                    : undefined
                                                }
                                                style={{ color: 'inherit' }}
                                                ellipsis
                                              >
                                                {form.getFieldValue(['stages', name, 'name']) ||
                                                  t('Nome da etapa')}
                                              </Typography.Text>
                                            </Col>
                                            <Col span={2}>
                                              <CaretUpFilled
                                                title={t(`Mover {{stageName}} para cima`, {
                                                  stageName:
                                                    form.getFieldValue(['stages', name, 'name']) ||
                                                    t('Nome da etapa'),
                                                })}
                                                onClick={() => {
                                                  if (isGeneratingInterviewScript) return;
                                                  if (idx - 1 > 1) {
                                                    move(idx, idx - 1);
                                                  }
                                                }}
                                              />
                                            </Col>
                                            <Col span={2}>
                                              <CaretDownFilled
                                                title={t(`Mover {{stageName}} para baixo`, {
                                                  stageName:
                                                    form.getFieldValue(['stages', name, 'name']) ||
                                                    t('Nome da etapa'),
                                                })}
                                                onClick={() => {
                                                  if (isGeneratingInterviewScript) return;
                                                  if (idx + 1 < fields.length - 2) {
                                                    move(idx, idx + 1);
                                                  }
                                                }}
                                              />
                                            </Col>
                                          </Row>
                                        ),
                                      }
                                    : form.getFieldValue(['stages', name, 'type']) === 'APPROVED'
                                    ? [
                                        {
                                          key: 'addStage',
                                          label: (
                                            <Button
                                              type="dashed"
                                              onClick={() => {
                                                add(
                                                  {
                                                    name: t('Nova etapa'),
                                                    key: fields.length.toString(),
                                                    type: 'CUSTOM',
                                                    interview_script_template: 0,
                                                    email_communication_template: 0,
                                                    message: '',
                                                    attachments: [],
                                                    fixed: false,
                                                    fromName: '',
                                                    interview_script_content: '',
                                                    interview_script_name: '',
                                                    lock_interview_script: false,
                                                    sub_type: '',
                                                    subject: '',
                                                  },
                                                  idx
                                                );
                                                setSelectedStageIdx(idx);
                                              }}
                                              block
                                              icon={<PlusOutlined />}
                                              disabled={isGeneratingInterviewScript}
                                            >
                                              {' '}
                                              <Trans>Adicionar etapa</Trans>
                                            </Button>
                                          ),
                                        },
                                        {
                                          key: form.getFieldValue(['stages', name, 'type']),
                                          danger:
                                            form
                                              .getFieldsError()
                                              .filter(
                                                (error) =>
                                                  ['testTypeList', 'subject', 'message'].includes(
                                                    error.name[0] as string
                                                  ) && error.errors.length
                                              ).length > 0,
                                          disabled: !(
                                            form.getFieldValue(['stages', name, 'type']) ===
                                            'SUBSCRIBED'
                                          ),
                                          label: form.getFieldValue(['stages', name, 'name']),
                                        },
                                      ]
                                    : {
                                        key,
                                        danger:
                                          form
                                            .getFieldsError()
                                            .filter(
                                              (error) =>
                                                ['testTypeList', 'subject', 'message'].includes(
                                                  error.name[0] as string
                                                ) && error.errors.length
                                            ).length > 0,
                                        disabled: !(
                                          form.getFieldValue(['stages', name, 'type']) ===
                                          'SUBSCRIBED'
                                        ),
                                        label: form.getFieldValue(['stages', name, 'name']),
                                      }
                                )}
                              />
                            )}
                          </Form.Item>
                        </Form.Col>
                        <Form.Col span={18}>
                          {fields.map(({ key, name }, idx) => (
                            <div hidden={idx !== selectedStageIdx} key={key}>
                              <StageFormItem
                                tests={tests}
                                namePrefix={[name]}
                                jobPostingId={jobPosting.data?.id}
                                isDescriptionEmpty={isDescriptionEmpty(
                                  jobPosting.data?.description
                                )}
                                fixedName={form.getFieldValue(['stages', name, 'fixed'])}
                                additionalQuestionsForm={
                                  form.getFieldValue(['stages', name, 'type']) === 'SUBSCRIBED'
                                }
                                registrationTemplateForm={
                                  form.getFieldValue(['stages', name, 'type']) === 'SUBSCRIBED'
                                }
                                templateForm={
                                  form.getFieldValue(['stages', name, 'type']) === 'SUBSCRIBED'
                                }
                                interviewScriptTemplates={interviewScriptTemplates}
                                isGeneratingInterviewScriptState={[
                                  isGeneratingInterviewScript,
                                  setIsGeneratingInterviewScript,
                                ]}
                                setIsDirty={setIsDirty}
                                enableChangeLanguage={
                                  config.data?.t007_enableChangeLanguage || false
                                }
                              />
                            </div>
                          ))}
                        </Form.Col>
                      </>
                    )}
                  </Form.List>
                </Form.Row>
                <Form.Footer>
                  <Form.FooterEnd>
                    <Button
                      onClick={() =>
                        navigate({
                          pathname: '/:tenant/opportunities/:id/',
                          params: { tenant, id },
                        })
                      }
                    >
                      <Trans>Cancelar</Trans>
                    </Button>
                    <Button
                      htmlType="submit"
                      loading={mutation.isLoading}
                      onClick={() => {
                        searchParams.delete('new');
                        setLeavePage(true);
                      }}
                    >
                      <Trans>Salvar e Sair</Trans>
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={mutation.isLoading}
                      disabled={mutation.isLoading}
                    >
                      {searchParams.get('new') ? t('Salvar e continuar') : t('Salvar')}
                    </Button>
                  </Form.FooterEnd>
                </Form.Footer>
              </Space>
            </Form>
          </Form.Provider>
          <div style={{ height: 80 }} />
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default StagesForm;
